import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { FormInput } from '../components/common/FormInput';
import { getStockById, updateStock } from '../services/stocks';

export const StockEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(!location.state?.stock);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    quantity: '',
    unit_price: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchStock = async () => {
      try {
        
        // Use stock data from location state if available, otherwise fetch from API
        const stockData = location.state?.stock || await getStockById(id);
        
        setFormData({
          name: stockData.name || '',
          category: stockData.category || '',
          quantity: stockData.quantity || '',
          unit_price: stockData.unit_price || '',
        });
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load stock');
      } finally {
        setLoading(false);
      }
    };

    fetchStock();
  }, [id, location.state?.stock]);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateStock = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Product name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    if (!formData.unit_price) newErrors.unit_price = 'Unit price is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        quantity: parseInt(formData.quantity),
        unit_price: formData.unit_price,
      };

      await updateStock(id, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/stocks');
      }, 1500);
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to update stock' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading stock...</p>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error && !formData.name) {
    return (
      <AuthenticatedLayout>
        <div className="space-y-6">
          <button
            onClick={() => navigate('/stocks')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Stocks
          </button>

          <Card>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          </Card>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/stocks')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Stocks
          </button>
        </div>

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Edit Stock Item</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">Update the product details</p>
        </div>

        <Card>
          <form onSubmit={handleUpdateStock} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <FormInput
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={errors.name}
                required
              />
              <FormInput
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                error={errors.category}
                placeholder="e.g., Electronics, Clothing, etc."
                required
              />
              <FormInput
                label="Quantity"
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleFormChange}
                error={errors.quantity}
                required
              />
              <FormInput
                label="Unit Price"
                type="number"
                name="unit_price"
                value={formData.unit_price}
                onChange={handleFormChange}
                error={errors.unit_price}
                step="0.01"
                required
              />
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-red-800">{errors.submit}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                Stock item updated successfully!
              </div>
            )}

            <div className="flex gap-4">
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Stock Item'}
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => navigate('/stocks')}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </AuthenticatedLayout>
  );
};
